/**
 * GLSL for evaluating lighting contribution light offset maps - a novel deferred rendering method that stores point light data in a low resolution 3D grid.
 * @module
 */
import type { DesignMaterialShaderChunks } from "../designMaterialShaderChunks";
import { safeShaderReplace, type ShaderProgramLike } from "../shaderUtils";
import { ensureWorldPositionInFragShader } from "./ensureWorldPositionInFragShader";

export const LIGHT_OFFSET_MAP_MAX_XY_DISTANCE_MULTIPLIER = 10;

export const OFFSET_MAP_FADE_OUT_DISTANCE_ABOVE = 30;

// Related to lights_fragment_maps shader chunk in upstream.
export const lightOffsetMapParsFragment = `
// These can be used to get the light map sample position from the world position.
// We use these instead of a transformation matrix since it saves us some uniform slots.
uniform vec3 lightOffsetMapWorldSize;
uniform vec3 lightOffsetMapWorldOffset;

// Each pixel in this texture contains normalized xyz offset for one light.
uniform sampler2D lightOffsetMap;

// Values shared among all lights in the light offset map:
uniform vec3 lightOffsetMapScale;  // normalized xyz offsets should be multiplied by this value to get offset in world coordinates.

// Number of grid cells that are stored in the texture. The interleaving factor affects the size of the actual texture data.
uniform vec3 lightOffsetMapGridSize;

uniform vec3 lightOffsetMapColor;
uniform float lightOffsetMapDistance;
uniform float lightOffsetMapDecay;

const float fadeOutMultiplierAbove = ${(1.0 / OFFSET_MAP_FADE_OUT_DISTANCE_ABOVE).toFixed(3)};

float getCustomDistanceAttenuation(const in vec3 worldLightToGeom, const in float lightXYDistanceWeight) {

  // World space
  float lightDistance = length(worldLightToGeom * vec3(lightXYDistanceWeight, lightXYDistanceWeight, 1.0));
  float zOffset = worldLightToGeom.z;
  float zOffsetMultiplier = saturate(1.0 - zOffset * fadeOutMultiplierAbove); // Shape the light to prevent leaking to floor above

  return pow(saturate(1.0 - lightDistance / lightOffsetMapDistance), lightOffsetMapDecay) * zOffsetMultiplier;

}

// light is an out parameter as having it as a return value caused compiler errors on some devices
void getCustomPointLightInfo(const in vec3 lightPosition, const in vec3 geometryPosition, const in vec3 worldLightToGeom, const in float lightXYDistanceWeight, out IncidentLight light) {

  // View space
  vec3 lVector = lightPosition - geometryPosition;
  light.direction = normalize(lVector);

  light.color = lightOffsetMapColor;
  light.color *= getCustomDistanceAttenuation(worldLightToGeom, lightXYDistanceWeight);
  light.visible = (light.color != vec3(0.0));

}
`;

export function lightOffsetMapFragment(offsetMapLightCount: number) {
  return `

vec3 lightOffsetMapGridLocation = (vWorldPosition.xyz - lightOffsetMapWorldOffset) / lightOffsetMapWorldSize;

// We take a nearest sample from the light offset map since linear filtering would result in artifacts at discontinuities.
// In order to avoid pixelation, we compute the world space position of the pixel center and compute the light positions based on that.
vec3 roundedLightOffsetMapGridLocation = clamp(floor(lightOffsetMapGridLocation * lightOffsetMapGridSize), vec3(0.0), lightOffsetMapGridSize - 1.0);

vec3 offsetToGridCellCenter = (roundedLightOffsetMapGridLocation + 0.5) / lightOffsetMapGridSize - lightOffsetMapGridLocation;

vec3 worldPosOfGridCellCenter = vWorldPosition.xyz + offsetToGridCellCenter * lightOffsetMapWorldSize;

// To support more simultaneous lights, pixels are interleaved in the offset map.
const int interleavedPixels = ${offsetMapLightCount};
vec2 lightOffsetMapPixelSize = vec2(1.0 / (lightOffsetMapGridSize.x * float(interleavedPixels)), 1.0 / (lightOffsetMapGridSize.y * lightOffsetMapGridSize.z));
for (int interleaveIndex = 0; interleaveIndex < interleavedPixels; ++interleaveIndex) {
  vec2 lightOffsetMapUv = vec2(roundedLightOffsetMapGridLocation.x * float(interleavedPixels) + float(interleaveIndex) + 0.5,
                                roundedLightOffsetMapGridLocation.z * lightOffsetMapGridSize.y + roundedLightOffsetMapGridLocation.y + 0.5)
                           * lightOffsetMapPixelSize;
  vec4 lightOffsetMapTexel = texture2DLodEXT(lightOffsetMap, lightOffsetMapUv, 0.0);

  if (lightOffsetMapTexel.x < 1.0) {
    vec3 lightWorldPosition = worldPosOfGridCellCenter + (lightOffsetMapTexel.xyz - 127.0 / 255.0) * lightOffsetMapScale;
    float lightXYDistanceWeight = lightOffsetMapTexel.w * ${LIGHT_OFFSET_MAP_MAX_XY_DISTANCE_MULTIPLIER.toFixed(1)};
    vec3 worldLightToGeom = vWorldPosition.xyz - lightWorldPosition;
    vec3 lightViewPosition = (viewMatrix * vec4(lightWorldPosition, 1.0)).xyz;
    getCustomPointLightInfo(lightViewPosition, geometryPosition, worldLightToGeom, lightXYDistanceWeight, directLight);
    RE_Direct(directLight, geometryPosition, geometryNormal, geometryViewDir, geometryClearcoatNormal, material, reflectedLight);
  }
}
`;
}

export function includeLightOffsetMap(
  shader: ShaderProgramLike,
  designMaterialShaderChunks: Pick<DesignMaterialShaderChunks, "fragmentDefinitions">,
  offsetMapLightCount: number,
) {
  ensureWorldPositionInFragShader(shader);
  designMaterialShaderChunks.fragmentDefinitions.push(lightOffsetMapParsFragment);
  // We may have already replaced the inclusion of the lights_fragment_maps chunk to support light probe grids.
  shader.fragmentShader = safeShaderReplace(
    shader.fragmentShader,
    "#include <lights_fragment_end>",
    `${lightOffsetMapFragment(offsetMapLightCount)}
      #include <lights_fragment_end>`,
    "includeLightOffsetMap (lights_fragment_end)",
  );
}
